// Round 40c — Admin "Questions" page rebuilt to mirror the user-side
// Dashboard chat-session pattern: each chat session is one row whose
// title is the first question; clicking opens a slide-in drawer with
// the full Guruji conversation, bubble-by-bubble.
//
// Replaces the legacy per-question table that surfaced one row per
// /api/astrology/ask payload — that worked for the old Q&A product
// but no longer reflects the live chat product. The backend pair of
// /api/admin/chat/sessions + /api/admin/chat/sessions/{id} provides
// the data; the legacy /admin/questions endpoint is left intact for
// any older clients but is no longer the UI's source of truth.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
    Search, Clock, User as UserIcon, X, Loader2, ArrowRight,
    MessageCircle, Heart,
} from 'lucide-react';

// Verdict badge colour palette — matches user Dashboard.
const VERDICT_STYLES = {
    YES:             'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    NO:              'bg-rose-500/10 text-rose-400 border-rose-500/30',
    YES_WITH_DELAY:  'bg-amber-500/10 text-amber-300 border-amber-500/30',
    NO_BUT_LATER:    'bg-sky-500/10 text-sky-400 border-sky-500/30',
    YES_BUT_DELAY:   'bg-amber-500/10 text-amber-300 border-amber-500/30',
    // Round 47c — compatibility v2 4-tier labels
    'Made for Each Other':   'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
    'A Good Match':          'bg-marigold/15 text-marigold border-marigold/40',
    'Has Its Challenges':    'bg-amber-500/10 text-amber-300 border-amber-500/30',
    'Needs Careful Thought': 'bg-rose-500/10 text-rose-300 border-rose-500/30',
};

const STATUS_LABEL = {
    active:    'In progress',
    ended:     'Ended',
    timeout:   'Timed out',
    abandoned: 'Abandoned',
    paused:    'Paused',
};

export default function AdminQuestions() {
    const [q, setQ] = useState('');
    const [data, setData] = useState({ rows: [], total: 0, chats: 0, compat: 0 });
    const [loading, setLoading] = useState(true);
    const [openSessionId, setOpenSessionId] = useState(null);
    const navigate = useNavigate();

    const load = async () => {
        setLoading(true);
        try {
            // Round 47c — Readings are now a merged timeline of chat
            // sessions + compatibility readings. We fan out both queries
            // in parallel and sort the union by `started_at` / `created_at`.
            const [chatRes, compatRes] = await Promise.allSettled([
                api.get('/admin/chat/sessions',     { params: { q: q || undefined, limit: 50 } }),
                api.get('/admin/compatibility/list', { params: { q: q || undefined, limit: 50 } }),
            ]);
            const chats = chatRes.status === 'fulfilled'
                ? (chatRes.value.data?.rows  || []).map((r) => ({ ...r, kind: 'chat',
                    __sort: r.started_at || '' }))
                : [];
            const compats = compatRes.status === 'fulfilled'
                ? (compatRes.value.data?.rows || []).map((r) => ({ ...r, kind: 'compatibility',
                    __sort: r.created_at || '' }))
                : [];
            const merged = [...chats, ...compats].sort(
                (a, b) => (b.__sort || '').localeCompare(a.__sort || ''),
            );
            setData({
                rows:   merged,
                total:  merged.length,
                chats:  chatRes.status === 'fulfilled'  ? (chatRes.value.data?.total  || 0) : 0,
                compat: compatRes.status === 'fulfilled' ? (compatRes.value.data?.total || 0) : 0,
            });
        } finally { setLoading(false); }
    };

    useEffect(() => {
        const t = setTimeout(load, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);

    return (
        <div className="p-6 lg:p-10 space-y-6" data-testid="admin-questions">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="overline">Readings</div>
                    <h1 className="font-serif text-3xl text-parchment mt-2" data-testid="admin-sessions-total">
                        {data.total} reading{data.total === 1 ? '' : 's'}
                    </h1>
                    <p className="text-xs text-slate-500 mt-1">
                        {data.chats} chat session{data.chats === 1 ? '' : 's'} ·{' '}
                        {data.compat} compatibility reading{data.compat === 1 ? '' : 's'}
                    </p>
                </div>
                <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by question text…"
                        data-testid="questions-search"
                        className="pl-9 w-72 bg-elevated border-marigold/15 text-parchment rounded-sm"
                    />
                </div>
            </div>

            {loading && (
                <Card className="bg-surface border-marigold/10 rounded-sm p-10 text-center text-slate-500" data-testid="admin-sessions-loading">
                    <Loader2 className="animate-spin mx-auto mb-2 text-marigold" size={20} />
                    Loading sessions…
                </Card>
            )}

            {!loading && data.rows.length === 0 && (
                <Card className="bg-surface border-marigold/10 rounded-sm p-12 text-center" data-testid="admin-sessions-empty">
                    <p className="text-slate-400">No readings match your search.</p>
                </Card>
            )}

            {!loading && data.rows.length > 0 && (
                <div className="space-y-4" data-testid="admin-sessions-list">
                    {data.rows.map((s) => (
                        s.kind === 'compatibility'
                            ? <CompatRow key={`compat-${s.id}`} s={s} onOpen={() => navigate(`/app/compatibility?check_id=${s.id}`)} />
                            : <ChatRow   key={`chat-${s.id}`}   s={s} onOpen={() => setOpenSessionId(s.id)} />
                    ))}
                </div>
            )}

            {openSessionId && (
                <SessionDrawer
                    sessionId={openSessionId}
                    onClose={() => setOpenSessionId(null)}
                />
            )}
        </div>
    );
}

function ChatRow({ s, onOpen }) {
    return (
        <button
            type="button"
            onClick={onOpen}
            data-testid={`admin-session-row-${s.id}`}
            className="w-full text-left"
        >
            <Card className="bg-surface border-marigold/10 rounded-sm p-6 hover:bg-elevated/60 transition-colors flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                        <Badge className="rounded-sm border bg-marigold/10 text-marigold border-marigold/30 inline-flex items-center gap-1">
                            <MessageCircle size={10} /> Chat
                        </Badge>
                        {s.verdict_label && (
                            <Badge className={`rounded-sm border ${VERDICT_STYLES[s.verdict_label] || 'bg-slate-500/10 text-slate-300 border-slate-500/30'}`}>
                                {String(s.verdict_label).replace(/_/g, ' ')}
                            </Badge>
                        )}
                        {s.verdict_category && (
                            <span className="text-xs text-slate-500 uppercase tracking-wider">{s.verdict_category}</span>
                        )}
                        {s.horary_number && (
                            <>
                                <span className="text-xs text-slate-600">·</span>
                                <span className="text-xs text-slate-500">horary #{s.horary_number}</span>
                            </>
                        )}
                        <span className="text-xs text-slate-600">·</span>
                        <span className="text-xs text-slate-500">{STATUS_LABEL[s.status] || s.status}</span>
                        {typeof s.rating === 'number' && (
                            <>
                                <span className="text-xs text-slate-600">·</span>
                                <span className={`text-xs ${s.rating >= 4 ? 'text-emerald-400' : s.rating === 3 ? 'text-amber-300' : 'text-rose-400'}`}>
                                    {'★'.repeat(s.rating)}{'☆'.repeat(5 - s.rating)}
                                </span>
                            </>
                        )}
                    </div>
                    <p className="font-serif text-lg md:text-xl text-parchment leading-snug line-clamp-2">
                        {s.first_question || '(no question recorded)'}
                    </p>
                    <div className="mt-2 text-xs text-slate-500 flex flex-wrap items-center gap-3">
                        <span className="inline-flex items-center gap-1">
                            <UserIcon size={11} className="text-marigold" />
                            {s.user_email || s.user_id}
                        </span>
                        {s.profile_name && s.profile_name !== s.user_name && (
                            <span>· for {s.profile_name}</span>
                        )}
                        <span className="inline-flex items-center gap-1">
                            <Clock size={11} />
                            {s.started_at ? new Date(s.started_at).toLocaleString() : '—'}
                        </span>
                        {s.tick_count ? <span>· {s.tick_count} min</span> : null}
                        {s.total_deducted_inr ? <span>· ₹{Number(s.total_deducted_inr).toFixed(2)}</span> : null}
                    </div>
                </div>
                <div className="text-xs text-marigold inline-flex items-center gap-1 shrink-0">
                    View chat <ArrowRight size={12} />
                </div>
            </Card>
        </button>
    );
}

function CompatRow({ s, onOpen }) {
    const a = s.profile_name || s.user_name || s.user_email || 'You';
    const b = s.partner_name || 'Partner';
    const amount = s.amount_charged_inr != null
        ? `₹${Number(s.amount_charged_inr).toFixed(2)}`
        : (s.amount_charged_paise != null
            ? `${s.currency === 'USD' ? '$' : '₹'}${(Number(s.amount_charged_paise) / 100).toFixed(2)}`
            : null);
    return (
        <button
            type="button"
            onClick={onOpen}
            data-testid={`admin-compat-row-${s.id}`}
            className="w-full text-left"
        >
            <Card className="bg-surface border-marigold/10 rounded-sm p-6 hover:bg-elevated/60 transition-colors flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                        <Badge className="rounded-sm border bg-rose-500/10 text-rose-300 border-rose-500/30 inline-flex items-center gap-1">
                            <Heart size={10} /> Compatibility
                        </Badge>
                        {s.verdict_label && (
                            <Badge className={`rounded-sm border capitalize ${VERDICT_STYLES[s.verdict_label] || 'bg-slate-500/10 text-slate-300 border-slate-500/30'}`}>
                                {s.verdict_label}
                            </Badge>
                        )}
                        {s.schema_version && (
                            <span className="text-xs text-slate-500 uppercase tracking-wider">v{s.schema_version}</span>
                        )}
                    </div>
                    <p className="font-serif text-lg md:text-xl text-parchment leading-snug line-clamp-2">
                        {a} <span className="text-marigold/60 mx-1">&</span> {b}
                    </p>
                    <div className="mt-2 text-xs text-slate-500 flex flex-wrap items-center gap-3">
                        <span className="inline-flex items-center gap-1">
                            <UserIcon size={11} className="text-marigold" />
                            {s.user_email || s.user_id}
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <Clock size={11} />
                            {s.created_at ? new Date(s.created_at).toLocaleString() : '—'}
                        </span>
                        {amount && <span>· {amount}</span>}
                    </div>
                </div>
                <div className="text-xs text-marigold inline-flex items-center gap-1 shrink-0">
                    View reading <ArrowRight size={12} />
                </div>
            </Card>
        </button>
    );
}

function SessionDrawer({ sessionId, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        api.get(`/admin/chat/sessions/${sessionId}`)
            .then((r) => { if (alive) setData(r.data); })
            .catch(() => { if (alive) setData(null); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [sessionId]);

    const sess = data?.session;
    const user = data?.user;

    return (
        <div
            className="fixed inset-0 z-50 bg-obsidian/70 backdrop-blur-sm flex justify-end"
            onClick={onClose}
            data-testid="admin-session-drawer-overlay"
        >
            <div
                className="w-full max-w-xl bg-surface border-l border-marigold/15 h-full overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
                data-testid="admin-session-drawer"
            >
                <div className="sticky top-0 bg-surface/95 backdrop-blur border-b border-marigold/10 px-5 py-4 flex items-center justify-between z-10">
                    <div className="min-w-0">
                        <div className="text-xs text-marigold uppercase tracking-wider">Conversation with Guruji</div>
                        <div className="text-sm text-parchment line-clamp-1">{sess?.first_question || '…'}</div>
                        {user && (
                            <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">
                                {user.email}{user.name ? ` · ${user.name}` : ''}
                            </div>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="admin-session-drawer-close"
                        className="p-2 rounded-sm hover:bg-marigold/10 text-slate-400 hover:text-parchment shrink-0 ml-3"
                    >
                        <X size={18} />
                    </button>
                </div>

                {loading && (
                    <div className="p-10 flex items-center justify-center text-marigold">
                        <Loader2 className="animate-spin" size={20} />
                    </div>
                )}

                {!loading && data && (
                    <>
                        <div className="px-5 py-3 border-b border-marigold/5 text-[11px] text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                            <span>Status: <span className="text-parchment">{STATUS_LABEL[sess?.status] || sess?.status || '—'}</span></span>
                            {sess?.tick_count != null && <span>· {sess.tick_count} min</span>}
                            {sess?.total_deducted_inr != null && <span>· ₹{Number(sess.total_deducted_inr).toFixed(2)} spent</span>}
                            {sess?.horary_number && <span>· horary #{sess.horary_number}</span>}
                            {typeof sess?.rating === 'number' && (
                                <span>· rated {sess.rating}★{sess.rating_comment ? ` "${sess.rating_comment}"` : ''}</span>
                            )}
                        </div>

                        <div className="px-5 py-4 space-y-3">
                            {(data.messages || []).map((m) => <SessionBubble key={m.id} m={m} />)}
                            {(data.messages || []).length === 0 && (
                                <div className="text-slate-500 italic text-sm">No messages saved for this session.</div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function SessionBubble({ m }) {
    const isUser = m.role === 'user';
    const c = m.content || {};
    const bubbles = Array.isArray(c.bubbles) ? c.bubbles : null;
    const chips = Array.isArray(c.chips) ? c.chips : null;
    const text = c.text || c.reply || '';
    return (
        <div className={isUser ? 'flex justify-end' : 'flex justify-start'}>
            <div className={`max-w-[88%] rounded-sm p-3 text-sm whitespace-pre-wrap ${
                isUser
                    ? 'bg-marigold/15 text-parchment border border-marigold/30'
                    : 'bg-elevated text-slate-200 border border-marigold/10'
            }`}>
                {text && <div>{text}</div>}
                {bubbles && bubbles.map((b, i) => (
                    <div key={i} className={i > 0 ? 'mt-2 pt-2 border-t border-marigold/10' : ''}>{b}</div>
                ))}
                {chips && chips.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                        {chips.map((chip, i) => (
                            <span key={i} className="px-2 py-0.5 rounded-full bg-marigold/10 text-marigold text-[10px] border border-marigold/20">{chip}</span>
                        ))}
                    </div>
                )}
                <div className="mt-1.5 text-[10px] text-slate-500">{new Date(m.created_at).toLocaleString()}</div>
            </div>
        </div>
    );
}
